import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Spinner } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { passwordResetApi } from '../api/password-reset';

const requestSchema = z.object({ identity: z.string().email() });
const confirmSchema = z.object({
  code: z.string().min(4),
  newPassword: z.string().min(8),
});

type RequestValues = z.infer<typeof requestSchema>;
type ConfirmValues = z.infer<typeof confirmSchema>;

export function ForgotPasswordForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState<string | null>(null);

  const requestMutation = useMutation({
    mutationFn: (values: RequestValues) =>
      passwordResetApi.request(values.identity).then(() => values.identity),
    onSuccess: (id) => setIdentity(id),
  });

  const confirmMutation = useMutation({
    mutationFn: (values: ConfirmValues) =>
      passwordResetApi.confirm(identity!, values.code, values.newPassword),
    onSuccess: () => navigate(ROUTES.login, { replace: true }),
  });

  const requestForm = useForm<RequestValues>({ resolver: zodResolver(requestSchema) });
  const confirmForm = useForm<ConfirmValues>({ resolver: zodResolver(confirmSchema) });

  if (!identity) {
    return (
      <form
        onSubmit={requestForm.handleSubmit((v) => requestMutation.mutate(v))}
        className="space-y-4"
      >
        <FormField
          label={t('auth.login.email')}
          htmlFor="identity"
          error={requestForm.formState.errors.identity?.message}
        >
          <Input id="identity" type="email" autoComplete="email" {...requestForm.register('identity')} />
        </FormField>
        <Button type="submit" className="w-full" disabled={requestMutation.isPending}>
          {requestMutation.isPending ? <Spinner /> : t('auth.forgot.request')}
        </Button>
      </form>
    );
  }

  return (
    <form
      onSubmit={confirmForm.handleSubmit((v) => confirmMutation.mutate(v))}
      className="space-y-4"
    >
      <p className="text-sm text-muted-foreground">{identity}</p>
      <FormField
        label={t('auth.forgot.code')}
        htmlFor="code"
        error={confirmForm.formState.errors.code?.message}
      >
        <Input id="code" autoComplete="one-time-code" {...confirmForm.register('code')} />
      </FormField>
      <FormField
        label={t('auth.forgot.newPassword')}
        htmlFor="newPassword"
        error={confirmForm.formState.errors.newPassword?.message}
      >
        <Input
          id="newPassword"
          type="password"
          autoComplete="new-password"
          {...confirmForm.register('newPassword')}
        />
      </FormField>
      <Button type="submit" className="w-full" disabled={confirmMutation.isPending}>
        {confirmMutation.isPending ? <Spinner /> : t('auth.forgot.confirm')}
      </Button>
    </form>
  );
}
